import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useScanExpense, type ScannedExpenseData } from '@/hooks/useApi';
import { type AxiosError } from 'axios';
import toast from 'react-hot-toast';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Module-level store for passing the scanned file to the create page
// (File objects can't be passed via router state)
let _scannedFile: File | null = null;
export function getScannedFile(): File | null {
  const f = _scannedFile;
  _scannedFile = null; // consume once
  return f;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

export default function ExpenseScanModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scanMutation = useScanExpense();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scanResult, setScanResult] = useState<ScannedExpenseData | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setPreview(null);
    setSelectedFile(null);
    setScanResult(null);
    setDragging(false);
  }, []);

  function handleClose() {
    resetState();
    onClose();
  }

  function validateFile(file: File): boolean {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error(t('expenses.scan.unsupportedType'));
      return false;
    }
    if (file.size > MAX_SIZE) {
      toast.error(t('expenses.scan.fileTooLarge'));
      return false;
    }
    return true;
  }

  /** Resize an image to max 1024px on its longest side before upload */
  function resizeImage(file: File): Promise<File> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 1024;
        let { width, height } = img;

        if (width <= MAX && height <= MAX) {
          resolve(file);
          return;
        }

        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              resolve(file);
            }
          },
          file.type,
          0.9,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  }

  async function handleFileSelect(file: File) {
    if (!validateFile(file)) return;

    const resized = await resizeImage(file);
    setSelectedFile(resized);
    setScanResult(null);

    if (resized.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(resized);
    } else {
      setPreview(null);
    }

    // Auto-scan
    doScan(resized);
  }

  function doScan(file: File) {
    setErrorDetail(null);
    scanMutation.mutate({ file, engine: 'gpt' }, {
      onSuccess: (data) => {
        setScanResult(data);
        toast.success(t('expenses.scan.success'));
      },
      onError: (err) => {
        const axiosErr = err as AxiosError<{ error?: string }>;
        const detail = axiosErr?.response?.data?.error || axiosErr?.message || 'Unknown error';
        setErrorDetail(detail);
        toast.error(t('expenses.scan.failed'));
        console.error('Scan error:', axiosErr?.response?.status, detail);
      },
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }

  function handleUseResults() {
    if (!scanResult) return;
    // Store the file so the create page can pick it up as the receipt
    _scannedFile = selectedFile;
    navigate('/expenses/new', { state: { scannedData: scanResult } });
    handleClose();
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl modal-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">{t('expenses.scan.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('expenses.scan.subtitle')}</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Upload area */}
          {!selectedFile && (
            <>
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                  dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
              >
                <div className="text-5xl mb-3">📄</div>
                <p className="text-gray-700 font-medium mb-1">{t('expenses.scan.dropzone')}</p>
                <p className="text-gray-400 text-sm mb-4">{t('expenses.scan.supportedFormats')}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('expenses.scan.uploadFile')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    📷 {t('expenses.scan.takePhoto')}
                  </button>
                </div>
              </div>

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = '';
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = '';
                }}
              />
            </>
          )}

          {/* Processing state */}
          {selectedFile && scanMutation.isPending && (
            <div className="text-center py-8">
              <div className="inline-block w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
              <p className="text-gray-700 font-medium">{t('expenses.scan.processing')}</p>
              <p className="text-gray-400 text-sm mt-1">{t('expenses.scan.processingHint')}</p>
              {preview && (
                <img src={preview} alt="Preview" className="mx-auto mt-4 max-h-40 rounded-lg opacity-50" />
              )}
            </div>
          )}

          {/* Error state */}
          {selectedFile && scanMutation.isError && (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-red-600 font-medium mb-2">{t('expenses.scan.failed')}</p>
              {errorDetail && (
                <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">{errorDetail}</p>
              )}
              <div className="flex gap-3 justify-center">
                <button className="btn btn-primary" onClick={() => doScan(selectedFile)}>
                  {t('expenses.scan.retry')}
                </button>
                <button className="btn btn-secondary" onClick={resetState}>
                  {t('expenses.scan.tryAnother')}
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {scanResult && (
            <div className="space-y-4">
              {/* Confidence indicator */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{t('expenses.scan.confidence')}:</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      scanResult.confidence >= 80 ? 'bg-green-500' : scanResult.confidence >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${scanResult.confidence}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{Math.round(scanResult.confidence)}%</span>
              </div>

              {/* Preview + data */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Document preview */}
                {preview && (
                  <div className="rounded-lg overflow-hidden border">
                    <img src={preview} alt="Document" className="w-full object-contain max-h-64" />
                  </div>
                )}

                {/* Extracted data summary */}
                <div className={`space-y-2 ${!preview ? 'md:col-span-2' : ''}`}>
                  {scanResult.supplierName && (
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('expenses.supplier')}</span>
                      <p className="font-medium">{scanResult.supplierName}</p>
                    </div>
                  )}
                  {scanResult.description && (
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('expenses.description')}</span>
                      <p className="text-sm">{scanResult.description}</p>
                    </div>
                  )}
                  {scanResult.date && (
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('expenses.date')}</span>
                      <p className="text-sm">{scanResult.date}</p>
                    </div>
                  )}
                  {scanResult.reference && (
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('expenses.reference')}</span>
                      <p className="text-sm">{scanResult.reference}</p>
                    </div>
                  )}
                  <hr className="my-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('expenses.netAmount')}</span>
                      <p className="font-medium">€{scanResult.netAmount || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('invoices.vat')}</span>
                      <p className="font-medium">€{scanResult.vatAmount || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 uppercase">{t('invoices.total')}</span>
                      <p className="font-bold text-lg">€{scanResult.totalAmount || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line items if present */}
              {scanResult.lineItems && scanResult.lineItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">{t('expenses.scan.detectedItems')}</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">{t('expenses.description')}</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-right p-2">{t('invoices.total')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {scanResult.lineItems.slice(0, 10).map((item, i) => (
                          <tr key={i}>
                            <td className="p-2">{item.description}</td>
                            <td className="text-right p-2">{parseFloat(String(item.quantity))}</td>
                            <td className="text-right p-2">€{item.totalPrice || item.unitPrice || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button className="btn btn-primary flex-1" onClick={handleUseResults}>
                  {t('expenses.scan.useResults')}
                </button>
                <button className="btn btn-secondary" onClick={resetState}>
                  {t('expenses.scan.tryAnother')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
